import { FC } from 'react';

import { assignInlineVars } from '@vanilla-extract/dynamic';

import { DisplaySymbol } from '@core';
import { ToggleAction, useToggle } from '@hooks';
import { Box, Button } from '@ui';

import { LiveAnalysisTableContext, Parameter, Tare } from './context';
import { LiveAnalysisTableHeader } from './LiveAnalysisTableHeader';
import { LiveAnalysisTableFooter, LiveAnalysisTableRow } from './LiveAnalysisTableRow';
import * as styles from './styles.css';
import { useLiveAnalysisTable } from './useLiveAnalysisTable';

type LiveAnalysisTableProps = { parameters: Parameter[]; data: Tare[]; sum: Tare; isSetup: boolean };

export const LiveAnalysisTableComponent: FC<LiveAnalysisTableProps> = ({ data, parameters, sum, isSetup }) => {
    const innerParameters =
        parameters.length === 0
            ? [{ id: '', label: '-', unit: '', min: 0, max: 0, displaySymbol: '-' as DisplaySymbol }]
            : parameters;
    const innerData = data.length === 0 ? [{}] : data;
    const columns = innerParameters.length;
    const rows = innerData.length;

    return (
        <LiveAnalysisTableContext.Provider value={{ data: innerData, parameters: innerParameters, sum, isSetup }}>
            <Box className={styles.grid} style={assignInlineVars({ [styles.columnsVar]: String(columns) })}>
                <LiveAnalysisTableHeader />
                {Array.from({ length: rows }).map((_, i) => (
                    <LiveAnalysisTableRow key={i} index={i} />
                ))}
                <LiveAnalysisTableFooter />
            </Box>
        </LiveAnalysisTableContext.Provider>
    );
};

const LiveAnalysisTableToggle = ({ isSetup, toggleIsSetup }: { isSetup: boolean; toggleIsSetup: ToggleAction }) => {
    return (
        <Box className={styles.toggleGroup}>
            <Button rounded variant={isSetup ? 'tertiary' : 'plain'} onClick={() => toggleIsSetup(true)}>
                P1
            </Button>
            <Button rounded variant={!isSetup ? 'tertiary' : 'plain'} onClick={() => toggleIsSetup(false)}>
                P2-P8
            </Button>
        </Box>
    );
};

export const LiveAnalysisTable = () => {
    const [isSetup, toggleIsSetup] = useToggle(true);
    const tableData = useLiveAnalysisTable(isSetup);

    return (
        <Box className={styles.tableWrapper}>
            <LiveAnalysisTableToggle isSetup={isSetup} toggleIsSetup={toggleIsSetup} />
            <LiveAnalysisTableComponent {...tableData} isSetup={isSetup} />
        </Box>
    );
};
